import React, { useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { Card, Form, Input, Button, Typography, message } from 'antd';
import { UserOutlined, LockOutlined } from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import { PLATFORM_ADMIN_HOME } from './staffConsolePaths';
import './platform-admin.css';

const { Title, Text } = Typography;

const PlatformAdminLogin: React.FC = () => {
  const navigate = useNavigate();
  const { login, token, role, teamId } = useAuth();
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();

  if (token && role === '超级管理员' && !teamId) {
    return <Navigate to={PLATFORM_ADMIN_HOME} replace />;
  }

  const onFinish = async (values: { username: string; password: string }) => {
    setLoading(true);
    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          platformAdmin: true,
          username: values.username,
          password: values.password
        })
      });

      const rawText = await response.text();
      let body: Record<string, unknown> | null = null;
      if (rawText && rawText.trimStart().startsWith('{')) {
        try {
          body = JSON.parse(rawText) as Record<string, unknown>;
        } catch {
          body = null;
        }
      }

      if (response.ok) {
        const data = body as {
          token?: string;
          user?: Record<string, unknown>;
          teamCode?: string;
        } | null;
        if (!data?.token || !data?.user) {
          message.error('登录响应异常');
          return;
        }
        const u = data.user as Record<string, unknown>;
        if (u.role !== '超级管理员') {
          message.error('该平台入口仅限超级管理员使用');
          return;
        }
        if (u.teamId) {
          message.error('该账号仍绑定团队，请使用实验室首页登录或联系运维将 teamId 置空');
          return;
        }
        login(
          data.token,
          u.role as string,
          u.username as string,
          u.name as string | undefined,
          u.avatar as string | undefined,
          u.isFirstLogin as boolean | undefined,
          u.passwordChanged as boolean | undefined,
          u._id as string | undefined,
          undefined,
          undefined
        );
        message.success('登录成功');
        navigate(PLATFORM_ADMIN_HOME);
        return;
      }

      const errText =
        (body?.error as string) ||
        (body?.message as string) ||
        `登录失败（HTTP ${response.status}）`;
      message.error(errText);
    } catch {
      message.error('网络异常');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="platform-admin-login-page">
      <Card className="platform-admin-login-card">
        <div className="staff-console-login-head">
          <div className="staff-console-login-badge">Authorized staff</div>
          <Title level={4} className="staff-console-login-title">
            内部控制台
          </Title>
          <Text className="staff-console-login-sub">
            仅限授权人员使用 · 请勿公开或转发本页地址
          </Text>
        </div>

        <Form form={form} layout="vertical" onFinish={onFinish} size="large">
          <Form.Item
            name="username"
            label="账号"
            rules={[{ required: true, message: '请输入用户名或学号/工号' }]}
          >
            <Input
              prefix={<UserOutlined />}
              placeholder="用户名 / 学号 / 手机 / 邮箱"
              autoComplete="username"
            />
          </Form.Item>
          <Form.Item
            name="password"
            label="密码"
            rules={[{ required: true, message: '请输入密码' }]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="密码"
              autoComplete="current-password"
            />
          </Form.Item>
          <Form.Item style={{ marginBottom: 8 }}>
            <Button type="primary" htmlType="submit" loading={loading} block size="large">
              登录
            </Button>
          </Form.Item>
        </Form>

        <div className="platform-admin-login-foot">
          <Text>
            实验室成员请使用对外站点首页登录。若您误入本页，请关闭窗口。
          </Text>
        </div>
      </Card>
    </div>
  );
};

export default PlatformAdminLogin;
